import { useState, useCallback } from 'react';

/**
 * useTranscription Hook
 * 
 * A custom hook to manage transcription state and operations.
 * Provides a clean API for handling transcribed text with append functionality.
 * 
 * Returns:
 * - transcribedText: string - The accumulated transcribed text
 * - isRecording: boolean - Current recording state
 * - error: string | null - Current error message
 * - appendText: (text) => void - Append new text to transcription
 * - clearText: () => void - Clear all transcribed text
 * - setRecordingState: (isRecording) => void - Update recording state
 * - setError: (error) => void - Set error message
 * - clearError: () => void - Clear error message
 */
const useTranscription = () => {
  const [transcribedText, setTranscribedText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [error, setErrorState] = useState(null);

  const appendText = useCallback((newText) => {
    if (newText && newText.trim()) {
      setTranscribedText(prev => {
        // Add space between sentences if previous text exists and doesn't end with space
        const needsSpace = prev && !prev.endsWith(' ') && !newText.startsWith(' ');
        return prev + (needsSpace ? ' ' : '') + newText;
      });
    }
  }, []);

  const clearText = useCallback(() => {
    setTranscribedText('');
  }, []);

  const setRecordingState = useCallback((recording) => {
    setIsRecording(recording);
  }, []);

  const setError = useCallback((err) => {
    const errorMessage = err instanceof Error ? err.message : String(err);
    setErrorState(errorMessage);
  }, []);

  const clearError = useCallback(() => {
    setErrorState(null);
  }, []);

  return {
    transcribedText,
    isRecording,
    error,
    appendText,
    clearText,
    setRecordingState,
    setError,
    clearError
  };
};

export default useTranscription;
